import React, {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import Icon from 'components/icon';
import { scrollToBlock } from 'js/scroll-utils';
import breakpoints from 'js/breakpoints';
import { ARTICLE_SCROLL_ANIMATION_DURATION } from '../../constants/animations';
import cn from 'classnames';
import slugify from 'slugify';

const ANCHOR_HEADINGS_SELECT_QUERY = '.article-page__content h2';
const HIGHLIGHT_ACTIVE_ANCHOR_DISTANCE_THRESHOLD = 250;

const ArticleAnchorMenu = forwardRef(
  (
    {
      extraOffsetDesktop = 0,
      extraOffsetMobile = 0,
      heading,
      onScrollToAnchorStarted,
    },
    ref
  ) => {
    const [anchorLinks, setAnchorLinks] = useState([]);
    const [activeAnchorId, setActiveAnchorId] = useState(null);
    const anchorLinksRef = useRef(anchorLinks);
    const [isExpanded, setIsExpanded] = useState(true);
    const syncActiveAnchorOnScrollDisabledRef = useRef(false);
    const anchorLinkClickTimeoutRef = useRef(null);

    useImperativeHandle(ref, () => ({
      closeAnchorMenu() {
        setIsExpanded(false);
      },
    }));

    const setActiveAnchor = anchorId => {
      setActiveAnchorId(anchorId);
    };

    const syncActiveAnchorOnScroll = () => {
      if (syncActiveAnchorOnScrollDisabledRef.current) {
        return;
      }

      let activeId = null;

      const currentAnchorLinks = anchorLinksRef.current;

      for (let index = 0; index < currentAnchorLinks.length; index++) {
        const anchorLink = currentAnchorLinks[index];
        const rect = anchorLink.targetElement.getBoundingClientRect();
        const topOffset = rect.top;

        if (topOffset <= HIGHLIGHT_ACTIVE_ANCHOR_DISTANCE_THRESHOLD) {
          const nextTarget = currentAnchorLinks[index + 1];
          if (
            !(nextTarget && nextTarget.targetElement) ||
            (nextTarget.targetElement &&
              nextTarget.targetElement.getBoundingClientRect().top >
                HIGHLIGHT_ACTIVE_ANCHOR_DISTANCE_THRESHOLD)
          ) {
            activeId = anchorLink.id;
            break;
          }
        }
      }

      setActiveAnchor(activeId);
    };

    const createAnchorMenu = () => {
      const h2Headings = document.querySelectorAll(
        ANCHOR_HEADINGS_SELECT_QUERY
      );

      const existingHeadings = new Set();

      const links = Array.from(h2Headings).map(headingNode => {
        let headingText = headingNode.textContent.trim();
        let headingId = slugify(headingText, {
          lower: true,
          strict: true,
        });
        const originalHeadingId = headingId;
        let counter = 1;

        const parentTargetClassName =
          headingNode.getAttribute('data-anchor-parent');
        const parentTarget = parentTargetClassName
          ? headingNode.closest(`.${parentTargetClassName}`)
          : null;

        while (existingHeadings.has(headingId)) {
          headingId = `${originalHeadingId}-${counter}`;
          counter++;
        }
        existingHeadings.add(headingId);

        headingNode.id = headingId;
        return {
          id: headingId,
          targetElement: parentTarget ? parentTarget : headingNode,
          text: headingText,
        };
      });

      setAnchorLinks(links);
    };

    const scrollToAnchor = id => {
      if (
        onScrollToAnchorStarted &&
        typeof onScrollToAnchorStarted === 'function'
      ) {
        onScrollToAnchorStarted();
      }

      if (anchorLinkClickTimeoutRef.current) {
        clearTimeout(anchorLinkClickTimeoutRef.current);
      }

      syncActiveAnchorOnScrollDisabledRef.current = true;
      window.history.pushState(null, null, `#${id}`);
      const targetAnchorLink = anchorLinks.find(link => link.id === id);

      if (targetAnchorLink) {
        scrollToBlock(
          targetAnchorLink.targetElement,
          true,
          breakpoints.mobileArticleSidebar()
            ? extraOffsetDesktop
            : extraOffsetMobile
        );
      }

      setActiveAnchor(id);

      anchorLinkClickTimeoutRef.current = setTimeout(() => {
        syncActiveAnchorOnScrollDisabledRef.current = false;

        if (!breakpoints.mobileArticleSidebar()) {
          setIsExpanded(false);
        }
      }, ARTICLE_SCROLL_ANIMATION_DURATION);
    };

    useEffect(() => {
      const hash = window.location.hash.slice(1);
      anchorLinksRef.current = anchorLinks;

      if (hash && anchorLinks && anchorLinks.length > 0) {
        setTimeout(() => {
          scrollToAnchor(hash);
        }, 500);
      }
    }, [anchorLinks]);

    useEffect(() => {
      window.addEventListener('DOMContentLoaded', createAnchorMenu);
      window.addEventListener('scroll', syncActiveAnchorOnScroll);

      return () => {
        window.removeEventListener('DOMContentLoaded', createAnchorMenu);
        window.removeEventListener('scroll', syncActiveAnchorOnScroll);
      };
    }, []);

    const onAnchorLinkClick = (e, id) => {
      e.preventDefault();
      scrollToAnchor(id);
    };

    const toggleExpanded = () => {
      setIsExpanded(prev => !prev);
    };

    return anchorLinks && anchorLinks.length > 0 ? (
      <div className="article-anchor-menu">
        <button
          onClick={toggleExpanded}
          className={cn('article-anchor-menu__trigger', {
            'article-anchor-menu__trigger_expanded': isExpanded,
          })}
        >
          {heading && (
            <span className="article-anchor-menu__heading">{heading}</span>
          )}
          <Icon
            name="chevron-down"
            className="article-anchor-menu__trigger-icon"
          />
        </button>
        <ul
          className={cn('article-anchor-menu__list', {
            'article-anchor-menu__list_expanded': isExpanded,
          })}
        >
          {anchorLinks.map(({ id, text }) => (
            <li
              key={id}
              className={cn('article-anchor-menu__item', {
                'article-anchor-menu__item_active': activeAnchorId === id,
              })}
            >
              <a
                href={`#${id}`}
                onClick={e => onAnchorLinkClick(e, id)}
                className={cn('article-anchor-menu__link', {
                  'article-anchor-menu__link_active': activeAnchorId === id,
                })}
              >
                <span className="article-anchor-menu__link-text"> {text}</span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    ) : null;
  }
);

ArticleAnchorMenu.viewModelMeta = {
  extraOffsetDesktop: 'ignore',
  extraOffsetMobile: 'ignore',
  onScrollToAnchorStarted: 'ignore',
};

ArticleAnchorMenu.propTypes = {
  heading: PropTypes.string,
  extraOffsetDesktop: PropTypes.number,
  extraOffsetMobile: PropTypes.number,
  onScrollToAnchorStarted: PropTypes.func,
};

export default ArticleAnchorMenu;
